import type { LiveStage } from "@hobbyshopify/backend-client";
import { BaseController, Rx, RxList, type SubtreeModel } from "subtree.js/core";
import type { AppDeps } from "../../../../app-deps";
import { inviteGuest, loadStage, muteGuest, removeGuest } from "../../../../core/ops/live-ops";
import {
  busyForm,
  type FormStatus,
  failedForm,
  idleForm,
  idleLoad,
  type LoadStatus,
} from "../../../../core/primitives/load-status";
import { Loader } from "../../../../core/primitives/loader";
import type { StudioGuest } from "../../../../core/services/broadcast-studio";

export class StageState {
  readonly status = new Rx<LoadStatus>(idleLoad);
  readonly stage = new Rx<LiveStage | null>(null);
  /** Mute and speaking come from the room, never from our own copy. */
  readonly guests = new RxList<StudioGuest>();
  readonly action = new Rx<FormStatus>(idleForm);
  readonly busyUserId = new Rx<string | null>(null);
}

export abstract class StageActions {
  abstract invite(userId: string): Promise<void>;
  abstract remove(userId: string): Promise<void>;
  abstract mute(userId: string): Promise<void>;
}

export type StageDeps = Pick<AppDeps, "client" | "authHandler" | "broadcastStudio">;

/** Buyers on the stream: the hands up, and the guests the host can mute or take off. */
export class StageController extends BaseController implements StageActions {
  readonly state = new StageState();
  readonly #loader: Loader<string, LiveStage>;

  constructor(
    subtree: SubtreeModel,
    readonly deps: StageDeps,
    readonly host: { streamId: string },
  ) {
    super();
    subtree.put(StageState, this.state);
    subtree.put(StageActions, this);
    this.#loader = this.own(
      new Loader({
        status: this.state.status,
        fetch: (streamId: string) => loadStage({ ...deps, streamId }),
        loaded: (stage) => {
          this.state.stage.value = stage;
        },
      }),
    );
    this.sync(() => {
      this.state.guests.value = deps.broadcastStudio.guests;
    }, [deps.broadcastStudio]);
    void this.reload();
  }

  reload = (): Promise<void> => this.#loader.load(this.host.streamId);

  invite = (userId: string) => this.#act(userId, inviteGuest);

  remove = (userId: string) => this.#act(userId, removeGuest);

  mute = (userId: string) => this.#act(userId, muteGuest);

  async #act(userId: string, op: typeof inviteGuest | typeof removeGuest | typeof muteGuest): Promise<void> {
    if (this.state.action.value.busy) return;
    this.state.action.value = busyForm;
    this.state.busyUserId.value = userId;
    const r = await op({ ...this.deps, streamId: this.host.streamId, userId });
    if (this.isDisposed) return;
    this.state.busyUserId.value = null;
    this.state.action.value = r.ok ? idleForm : failedForm(r.errors);
    await this.reload();
  }
}
